import { logger } from '@sentry/react';
import { getAccessContext } from '@/utils/accessChannel';

/** Sentry structured-log levels (logger.*). */
export type SentryLogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

/**
 * Generic wrapper over Sentry structured logging.
 *
 * `enableLogs` is turned on in Sentry.init (see src/main.tsx), so these map to
 * the Sentry Logs product. Use instead of scattering logger.<level> calls.
 *
 * `logger` is imported by name (not via `import * as Sentry`). After the
 * @sentry/react v8→v9 bump the Vite dep cache must be busted
 * (`rm -rf node_modules/.vite`, or `yarn dev --force`): a stale v8 prebundle
 * has no `logger` export at all, so the import resolves to undefined at runtime.
 * Guarded so a logging failure never crashes the calling component.
 *
 * @example
 *   sentryLog('info', 'Order created', { orderId: 'order_456' });
 *   sentryLog('error', 'Payment failed', { reason: 'card_declined' });
 */
export const sentryLog = (
  level: SentryLogLevel,
  message: string,
  context?: Record<string, unknown>,
) => {
  try {
    logger?.[level]?.(message, context);
  } catch {
    // Logging must never break the UI.
  }
};

/**
 * Log an SMP mutation failure (Mode B — pause / skip / cancel / etc. action
 * fails after the page loaded). Reuses `sentryLog` and auto-enriches with
 * `getAccessContext()`; pass `subscriptionId` for ticket correlation.
 *
 * Defaults to `error` level. Pass `'warn'` for expected user-input failures
 * (invalid discount code, bad address) so they don't pollute error logs.
 *
 * @example
 *   onError: (error) => {
 *     setShowErrorDialog(true);
 *     logSMPMutationError('pauseSubscription', error, {
 *       screen: 'SMPPauseSubscriptionPage', subscriptionId,
 *     });
 *   }
 */
export const logSMPMutationError = (
  action: string,
  error: unknown,
  context?: Record<string, unknown>,
  level: SentryLogLevel = 'error',
) => {
  sentryLog(level, 'SMP mutation error', {
    action,
    errorMessage: getErrorMessage(error),
    errorStatus: getErrorStatus(error),
    ...getAccessContext(),
    ...context,
  });
};

/** Human-readable message from an unknown query/axios error. */
export const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (error == null) return 'Unknown error';
  // Use the Error message only when it's non-empty; a blank message (Error's
  // default) falls through so we still capture something useful below.
  if (error instanceof Error && error.message) return error.message;
  // SMP/LOOP failures are often plain objects ({ statusCode, msg, ... }) with no
  // Error prototype — serialize the whole payload instead of losing it to a
  // generic fallback. Guarded against circular / non-serializable values.
  try {
    const json = JSON.stringify(error);
    if (json && json !== '{}') return json;
  } catch {
    // fall through to String()
  }
  return String(error);
};

/**
 * HTTP status from an error. Reads the axios shape (`response.status`) and
 * falls back to a top-level `statusCode` — SMP/LOOP throw plain objects that
 * carry the code there (e.g. the 422 payment-update error).
 */
export const getErrorStatus = (error: unknown): number | undefined => {
  const e = error as
    | { response?: { status?: unknown }; statusCode?: unknown }
    | null;
  const status = e?.response?.status ?? e?.statusCode;
  return typeof status === 'number' ? status : undefined;
};
